import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { TransactionPagination } from '@/components/transactions/TransactionPagination';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { RecurringPayment } from '@/lib/api';
import { recurringCycleAmount } from '@/lib/recurring/money';
import { cn } from '@/lib/utils';
import {
  applyCommitmentFilters,
  useCommitmentFilters,
  type GroupFilter,
  type StatusFilter,
} from './commitmentFilters';
import type { ConnectorBranding } from './ConnectorDot';
import { RecurringCommitmentRow, type RecurringCommitment } from './RecurringCommitmentRow';
import { RECURRING_TAXONOMY } from './taxonomy';

const PAGE_SIZE = 8;

const GROUP_FILTERS: { value: GroupFilter; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'assinaturas', label: RECURRING_TAXONOMY.assinaturas.label },
  { value: 'parcelamentos', label: 'Parcelas' },
  { value: 'fixas', label: RECURRING_TAXONOMY.fixas.label },
];

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'todas', label: 'Todas' },
  { value: 'paga', label: 'Pagas' },
  { value: 'na-fatura', label: 'Na fatura' },
  { value: 'prevista', label: 'Previstas' },
  { value: 'atrasada', label: 'Atrasadas' },
];

interface Props {
  commitments: readonly RecurringCommitment[];
  connectorFor?: (accountId: string | null) => ConnectorBranding | null;
  onOpenItem?: (item: RecurringPayment) => void;
  onDeleteRequest?: (item: RecurringPayment) => void;
}

/**
 * Every commitment in one list — the page's primary surface. Replaces the three collapsible
 * per-taxonomy cards, where only one could be open at a time and two thirds of the page was
 * therefore always hidden. Kind becomes a filter chip instead of a container.
 */
export function RecurringCommitmentsCard({
  commitments,
  connectorFor,
  onOpenItem,
  onDeleteRequest,
}: Props) {
  const formatCurrency = useFormatCurrency();
  const { filters, selectGroup, selectStatus, goToPage } = useCommitmentFilters();

  const visible = useMemo(
    () => applyCommitmentFilters(commitments, filters),
    [commitments, filters],
  );
  const selectionTotal = useMemo(
    () => visible.reduce((sum, commitment) => sum + recurringCycleAmount(commitment.item), 0),
    [visible],
  );

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(filters.page, totalPages);
  const pageItems = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const groupCount = (value: GroupFilter) =>
    value === 'todos'
      ? commitments.length
      : commitments.filter((commitment) => commitment.groupKey === value).length;
  const statusCount = (value: StatusFilter) =>
    value === 'todas'
      ? commitments.length
      : commitments.filter((commitment) => commitment.status === value).length;

  return (
    <Card
      variant="flat"
      className="rounded-[var(--radius-3xl)] border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6"
    >
      <div className="flex items-baseline justify-between gap-3">
        {/* Not just "Compromissos": the projection chart below is "Compromissos por mês", and two
            headings sharing a prefix read as the same thing. */}
        <h2 className="text-base font-semibold text-text-primary">Compromissos ativos</h2>
        <span className="font-mono text-2xs text-text-tertiary tabular-nums">
          {formatCurrency(selectionTotal, { noCents: true })}/mês nesta seleção
        </span>
      </div>

      <div
        role="radiogroup"
        aria-label="Filtrar por tipo"
        className="scrollbar-hide -mx-1 mt-3 flex gap-2 overflow-x-auto px-1 pb-1"
      >
        {GROUP_FILTERS.map((option) => (
          <FilterChip
            key={option.value}
            label={option.label}
            count={groupCount(option.value)}
            value={option.value}
            active={filters.group === option.value}
            onSelect={selectGroup}
          />
        ))}
      </div>

      <div
        role="radiogroup"
        aria-label="Filtrar por situação do pagamento"
        className="scrollbar-hide -mx-1 mt-2 flex gap-2 overflow-x-auto px-1 pb-1"
      >
        {STATUS_FILTERS.map((option) => (
          <FilterChip
            key={option.value}
            label={option.label}
            count={statusCount(option.value)}
            value={option.value}
            active={filters.status === option.value}
            onSelect={selectStatus}
          />
        ))}
      </div>

      <CommitmentList
        commitments={pageItems}
        connectorFor={connectorFor}
        onOpenItem={onOpenItem}
        onDeleteRequest={onDeleteRequest}
      />

      {/* The wrapper bleeds to the card's edges, so it may only exist when the pagination itself
          renders — otherwise its negative bottom margin eats the card's padding under the last row. */}
      {visible.length > PAGE_SIZE && (
        <div className="-mx-5 -mb-5 mt-1 sm:-mx-6 sm:-mb-6">
          <TransactionPagination
            totalCount={visible.length}
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            onPageChange={goToPage}
          />
        </div>
      )}
    </Card>
  );
}

function CommitmentList({
  commitments,
  connectorFor,
  onOpenItem,
  onDeleteRequest,
}: {
  commitments: readonly RecurringCommitment[];
  connectorFor?: (accountId: string | null) => ConnectorBranding | null;
  onOpenItem?: (item: RecurringPayment) => void;
  onDeleteRequest?: (item: RecurringPayment) => void;
}) {
  if (commitments.length === 0) {
    return <p className="py-10 text-center text-sm text-text-tertiary">Nenhum compromisso nesta seleção.</p>;
  }
  return (
    <ul className="-mx-2 mt-2 divide-y divide-divider" role="list" data-testid="recurring-commitments">
      {commitments.map((commitment) => (
        <RecurringCommitmentRow
          key={commitment.item.id}
          commitment={commitment}
          connectorFor={connectorFor}
          onOpenItem={onOpenItem}
          onDeleteRequest={onDeleteRequest}
        />
      ))}
    </ul>
  );
}

function FilterChip<T extends string>({
  label,
  count,
  value,
  active,
  onSelect,
}: {
  label: string;
  count: number;
  value: T;
  active: boolean;
  onSelect: (value: T) => void;
}) {
  const select = () => onSelect(value);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={select}
      className={cn(
        'shrink-0 rounded-full border px-3 py-1.5 text-2xs font-medium transition-colors',
        active
          ? 'border-brand-primary bg-brand-50 text-brand-800'
          : 'border-border bg-surface text-text-secondary hover:bg-surface-sunken',
      )}
    >
      {label} <span className="font-mono tabular-nums opacity-70">{count}</span>
    </button>
  );
}
